import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnDestroy,
    OnInit
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import _ from "lodash";
import { combineLatest, Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { Avverkningsformer, VirkeskopAtgardsgrupper } from "~app/common/models/constants";
import { FeatureService } from "~app/common/services/feature.service";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { VirkeskopValidationRuleDirective } from "../../../validation/directives/virkeskop-validation-rule.directive";
import { AtgardKedjaMediatorService } from "./../../../services/atgardkedja-mediator.service";

@Component({
    selector: "rask-skogsvard-detaljer-rojning",
    templateUrl: "./skogsvard-detaljer-rojning.component.html",
    styleUrls: ["./skogsvard-detaljer-rojning.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputNumberComponent,
        FormsModule,
        SimpleSelectorComponent,
        VirkeskopValidationRuleDirective,
        AsyncPipe
    ]
})
export class SkogsvardDetaljerRojningComponent implements OnInit, OnDestroy {
    protected efterfoljandeAtgardValues = computed(() => {
        const values = [
            Avverkningsformer.Gallring,
            Avverkningsformer.Foryngringsavverkning
        ];

        if (this.skordarmatningInforOvrigAvverkningActive()) {
            values.push(Avverkningsformer.OvrigAvverkning);
        }

        return values;
    });
    protected isFristaendeSiktrojning$: Observable<boolean>;
    protected kontraktReadonly = false;
    protected skogsvardsAtgard: Atgard;
    protected numberDecimalProcessor = InputProcessorType.numberDecimalProcessor(2, true);
    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly featureService = inject(FeatureService);
    private readonly skordarmatningInforOvrigAvverkningActive = toSignal(this.featureService.getFeatureActive("SiktrojningInforOvrigAvverkning"));

    private readonly ngDestroyed$ = new Subject<void>();

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(x => {
                this.skogsvardsAtgard = x;
            });

        const hasKoppladAvverkning$ = this.atgardMediatorService.atgard$.pipe(
            map((atg: Atgard) => {
                const kopplingar = this.atgardKedjaMediatorService.getAtgardKedjaKopplingarByAtgard(atg.guid)
                    ?.filter(k => k.atgardGuid !== atg.guid && k.atgardsgrupp === VirkeskopAtgardsgrupper.Avverkning);
                return kopplingar && kopplingar.length > 0;
            })
        );

        const isSiktrojning$ = this.atgardMediatorService.atgard$.pipe(
            map(atg => atg.isSiktrojning)
        );

        this.isFristaendeSiktrojning$ = combineLatest([
            hasKoppladAvverkning$,
            isSiktrojning$
        ]).pipe(
            map(([
                hasKoppladAvverkning,
                siktrojning
            ]) => !hasKoppladAvverkning && siktrojning)
        );
    }

    protected onEfterfoljandeAtgardChaged(efterfoljandeAtgard: string) {
        this.skogsvardsAtgard.efterfoljandeAtgard = efterfoljandeAtgard;
        this.atgardMediatorService.update(this.skogsvardsAtgard);
    }

    protected onNettoarealChanged($event): void {
        if (!_.isNil($event)) {
            this.kontraktMediatorService.updateNettoarealSrc.next({
                atgardGuid: this.skogsvardsAtgard.guid,
                nettoareal: this.skogsvardsAtgard.nettoareal,
                atgardstyp: this.skogsvardsAtgard.atgardstyp,
                avverkningsanmalanGodkantDatum: null,
                beteckning: this.skogsvardsAtgard.beteckning
            });

            this.update();
        }
    }

    private update(): void {
        this.atgardMediatorService.update(this.skogsvardsAtgard);
    }
}
